import { useMemo } from 'react';
import { ThresholdConfigSensor } from 'business-dashboard/src/api/thresholdsApi';
import { SensorTypes, ThresholdType } from 'commons/src/models/commonEnums';
import { CustomOptions, ThresholdOption } from '../../models/customThresholdModels';

export type UseThresholdSettingsProps = {
    onSave: (sensorValues: ThresholdConfigSensor) => void;
    thresholdOptions: ThresholdOption[];
    customOptions?: CustomOptions;
    scopeModified?: boolean;
    selectedValues: number[];
    sensorType: SensorTypes;
    isSaved?: boolean;
};

export const thresholdOrder: ThresholdType[] = Object.values(ThresholdType);
export type UseThresholdSettingsReturn = {
    sortedThresholdOptions: ThresholdOption[];
    notModified: boolean;
    validateInputValues: () => boolean;
    onSaveClicked: () => void;
};

const useThresholdSettings = ({
    onSave,
    thresholdOptions,
    customOptions,
    scopeModified,
    selectedValues,
    sensorType,
    isSaved,
}: UseThresholdSettingsProps): UseThresholdSettingsReturn => {
    const sortedThresholdOptions = useMemo(() => {
        if (thresholdOptions) {
            return [...thresholdOptions].sort(
                (a, b) => thresholdOrder.indexOf(a.type) - thresholdOrder.indexOf(b.type)
            );
        }
        return customOptions?.recommendedThresholdOptions || [];
    }, [customOptions, thresholdOptions]);

    const initialThresholds = useMemo(
        () => sortedThresholdOptions.map(option => option.value),
        [sortedThresholdOptions]
    );

    const notModified =
        isSaved || (!scopeModified && initialThresholds.every((value, index) => selectedValues[index] === value));

    const validateInputValues = (): boolean =>
        sortedThresholdOptions.every((_, index) => {
            const value = selectedValues[index];
            const isValidInArray = index === 0 || selectedValues[index - 1] < value;
            return value !== undefined && !Number.isNaN(value) && isValidInArray;
        });

    const onSaveClicked = (): void => {
        onSave({
            sensor: sensorType as keyof typeof SensorTypes,
            thresholds: Object.entries(selectedValues).map(([index, value]) => ({
                thresholdType: sortedThresholdOptions[parseInt(index, 10)].type,
                value,
            })),
        });
    };

    return {
        sortedThresholdOptions,
        notModified,
        validateInputValues,
        onSaveClicked,
    };
};

export default useThresholdSettings;
